import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { isProjectIdWellFormed, isWorkflowRecognizable } from 'state/utils';

import {
  BE_BIN_15_MINS,
  BIN_15_MINS,
  BIN_60_MINS,
  MAXIMIZE_DATA,
  MAXIMIZE_MAP,
  MapStyle,
  VIZ_MODE_CHART,
  VIZ_MODE_TABLE,
  WorkflowType,
} from 'appConstants';
import { LegendSettings } from 'appTypes';
import { replaceOrPushURLParam, resetURLParams } from 'appUtils';
import { DEFAULT_TIME } from 'features/common/DateTimeSelector/utils';
import { routeSelectionIsSavedRoute } from 'features/common/hooks/useRouteInfo';
import { isComparisonMonth } from 'features/task_bar/ComparisonPicker';
import { workflowLookup } from 'features/workflow_common/workflowLookup';

import { getProjectInfoForSlug, selectUserState } from './userSlice';

const kProjectParam = 'project';
const kDateParam = 'date';
const kTargetEndDateParam = 'dt_end';
const kWorkflowParam = 'workflow';
const kSelectedSegmentParam = 'segmentId';
const kRouteSelectedParam = 'route';
const kMapLatParam = 'lat';
const kMapLngParam = 'lng';
const kMapZoomParam = 'zoom';
const kRoadClassParam = 'roadCl';
const kTypicalMonthParam = 'typic';
const kComparisonParam = 'comp';
const kComparisonEndDateParam = 'cmp_end';
const kStartDateParam = 'start';
const kEndDateParam = 'end';
const kSlowdownThresholdParam = 'threshold';
const kMaximizeParam = 'max';
const kBinSizeParam = 'bin';
const kCapacityThresholdParam = 'capacity';
const kCapacityLanesParam = 'lanes';
const kVizModeParam = 'viz';
const kTimelineMetricParam = 'metric';
export const kScenarioIdParam = 'scenario';
export const kAnalysisIdParam = 'analysis';
export const kLegendSettingsParam = 'lege';
const kStartTimeParam = 'startT';
const kEndTimeParam = 'endT';
const kDayTypeParam = 'dtype';

const kDefaultWorkflow = undefined;
const kDefaultSlowdownThreshold = 0.65;
export const kDefaultCapacityThreshold = 600;
export const kDefaultCapacityLanes = 2;
export const kDefaultMapStyle = MapStyle.Dark;
const kMaxRouteSelectionHistory = 20;

const defaultLegendSettings: LegendSettings = {
  type: 'continuous',
  scheme: 'green',
};

const initialWorkflowState = {
  targetDate: null,
  // used for multi-day selection
  targetEndDate: undefined,
  segmentId: undefined,
  routeSelection: undefined,
  typicalMonth: null,
  maximize: null,
};

const initialSlowdownState = {
  slowdownThreshold: kDefaultSlowdownThreshold,
  comparison: undefined,
  comparisonEndDate: undefined,
};

const initialWorkflowTravelTimeToolbarState = {
  workflowTravelTimeToolbarUIState: undefined,
  routeSelectionHistory: [undefined],
};

const initialPlanningState = {
  binSize: null,
  capacityThreshold: kDefaultCapacityThreshold,
  capacityLanes: kDefaultCapacityLanes,
  vizMode: null,
};

const initialTimelinestate = {
  timeOfDay: 12 * 60,
  timelineMetric: undefined,
  showIncidents: false,
};

const initialPredictState = {
  ...initialTimelinestate,
  currentScenarioId: undefined,
  scenarioMode: undefined,
};

const initialRouteMonitoringState = {
  ...initialSlowdownState,
  startDateTime: undefined,
  endDateTime: undefined,
};

const initialODState = {
  ...initialTimelinestate,
  currentAnalysisId: undefined,
  startTime: DEFAULT_TIME,
  endTime: DEFAULT_TIME,
  dayType: undefined,
};
const initialQueuingState = {
  timeOfDay: undefined,
};

const workflowsInitialState = {
  workflows: {
    [WorkflowType.Plan]: { ...initialWorkflowState, ...initialPlanningState },
    [WorkflowType.TravelTime]: {
      ...initialWorkflowState,
      ...initialSlowdownState,
      ...initialWorkflowTravelTimeToolbarState,
    },
    [WorkflowType.Speed]: { ...initialWorkflowState, ...initialSlowdownState },
    [WorkflowType.Timeline]: {
      ...initialWorkflowState,
      ...initialTimelinestate,
    },
    [WorkflowType.Detouring]: {
      ...initialWorkflowState,
      ...initialPredictState,
    },
    [WorkflowType.RouteMonitoring]: {
      ...initialWorkflowState,
      ...initialRouteMonitoringState,
    },
    [WorkflowType.OriginDestination]: {
      ...initialWorkflowState,
      ...initialODState,
    },
    [WorkflowType.Queuing]: {
      ...initialWorkflowState,
      ...initialQueuingState,
    },
  },
  mapCenter: null,
  mapZoom: null,
  roadFilters: null,
  mapStyle: kDefaultMapStyle,
  showBarbs: false,
};

const initialState = {
  current: kDefaultWorkflow,
  project_slug: undefined,
  in_color_blind_mode: false,
  projects: {},
  legendSettings: defaultLegendSettings,
  hideSidebar: false,
};

export function getSearchParamsForState(state, wipe = false) {
  let searchParams;
  if (wipe) {
    searchParams = new URLSearchParams();
  } else {
    searchParams = new URLSearchParams(window.location.search);
  }
  function setParam(key, value) {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
  }

  const project = state.project_slug;
  if (project) {
    setParam(kProjectParam, project);
    if (!state.projects[project]) {
      return undefined;
    }
    if (state.current) {
      const workflowId = state.current;
      const project_state = state.projects[project];
      let workflow = project_state.workflows[workflowId];
      if (!workflow) {
        workflow = { ...workflowsInitialState.workflows[workflowId] };
      }
      setParam(kWorkflowParam, workflowId);
      if (workflow.targetDate) setParam(kDateParam, workflow.targetDate);
      if (workflow.targetEndDate) {
        setParam(kTargetEndDateParam, workflow.targetEndDate);
      } else {
        searchParams.delete(kTargetEndDateParam);
      }
      setParam(kSelectedSegmentParam, workflow.segmentId);
      let fmtedRouteSelection = workflow.routeSelection;
      if (
        fmtedRouteSelection &&
        !routeSelectionIsSavedRoute(fmtedRouteSelection)
      ) {
        fmtedRouteSelection = fmtedRouteSelection.join(',');
      }
      setParam(kRouteSelectedParam, fmtedRouteSelection);
      if (project_state.mapCenter) {
        setParam(kMapLatParam, Number(project_state.mapCenter.lat).toFixed(4));
        setParam(kMapLngParam, Number(project_state.mapCenter.lng).toFixed(4));
      }
      if (project_state.mapZoom) {
        setParam(kMapZoomParam, project_state.mapZoom);
      }
      if (project_state.roadFilters) {
        setParam(kRoadClassParam, project_state.roadFilters?.join('-'));
      }
      if (workflow.typicalMonth) {
        setParam(kTypicalMonthParam, workflow.typicalMonth);
      }
      if (workflow.comparison) {
        setParam(kComparisonParam, workflow.comparison);
      }
      if (workflow.comparisonEndDate) {
        setParam(kComparisonEndDateParam, workflow.comparisonEndDate);
      } else {
        searchParams.delete(kComparisonEndDateParam);
      }
      if (workflow.startDateTime) {
        setParam(kStartDateParam, workflow.startDateTime.slice(0, 13));
      }
      if (workflow.endDateTime) {
        setParam(kEndDateParam, workflow.endDateTime.slice(0, 13));
      }
      if (workflow.slowdownThreshold !== null) {
        setParam(kSlowdownThresholdParam, workflow.slowdownThreshold);
      }
      setParam(kMaximizeParam, workflow.maximize);
      // workflow-specific settings
      if (workflow.binSize) {
        setParam(kBinSizeParam, workflow.binSize);
      }
      if (workflow.capacityThreshold) {
        setParam(kCapacityThresholdParam, workflow.capacityThreshold);
      }
      if (workflow.capacityLanes) {
        setParam(kCapacityLanesParam, workflow.capacityLanes);
      }
      if (workflow.vizMode) {
        setParam(kVizModeParam, workflow.vizMode);
      }
      if (workflow.timelineMetric) {
        setParam(kTimelineMetricParam, workflow.timelineMetric);
      }

      if (workflow.dayType) {
        setParam(kDayTypeParam, workflow.dayType);
      }
      if (workflow.startTime) setParam(kStartTimeParam, workflow.startTime);
      if (workflow.endTime) setParam(kEndTimeParam, workflow.endTime);
      setParam(kScenarioIdParam, workflow.currentScenarioId);
      setParam(kAnalysisIdParam, workflow.currentAnalysisId);
    }
  }
  if (state.legendSettings) {
    setParam(
      kLegendSettingsParam,
      state.legendSettings.type[0] + state.legendSettings.scheme[0],
    );
  }
  return searchParams;
}

function setUrlFromState(state, wipe = false) {
  const searchParams = getSearchParamsForState(state, wipe);
  if (!searchParams) {
    return;
  }
  replaceOrPushURLParam(searchParams, [
    kWorkflowParam,
    kDateParam,
    kTargetEndDateParam,
    kComparisonParam,
    kComparisonEndDateParam,
    kProjectParam,
    kStartDateParam,
    kEndDateParam,
    kRouteSelectedParam,
    kLegendSettingsParam,
  ]);
}

export const getProjectState = (state, updateState = false) => {
  if (!state.project_slug) return undefined;
  const project = state.projects[state.project_slug];
  if (!project) {
    if (!updateState) {
      return undefined;
    } else {
      state.projects[state.project_slug] = structuredClone(
        workflowsInitialState,
      );
      return getProjectState(state);
    }
  }
  return project;
};

export const getSpecificWorkflowState = (
  state,
  workflow,
  updateState = false,
) => {
  const project = getProjectState(state, updateState);
  if (!project) {
    return undefined;
  }
  if (project.workflows[workflow] === undefined && updateState) {
    project.workflows[workflow] = {
      ...workflowsInitialState.workflows[workflow],
    };
  }
  return project.workflows[workflow];
};

export const getWorkflowState = (state, updateState = false) => {
  const project = getProjectState(state, updateState);
  if (!project) {
    return undefined;
  }
  if (project.workflows[state.current] === undefined && updateState) {
    project.workflows[state.current] = {
      ...workflowsInitialState.workflows[state.current],
    };
  }
  return project.workflows[state.current];
};

export const workflowSlice = createSlice({
  name: 'state',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    updateLegendSettingsGlobal: (
      state,
      action: { payload: Partial<LegendSettings> },
    ) => {
      state.legendSettings = {
        type: action.payload.type ?? state.legendSettings.type,
        scheme: action.payload.scheme ?? state.legendSettings.scheme,
      };
      setUrlFromState(state);
    },
    resetWorkflowState: (state) => {
      state.current = kDefaultWorkflow;
      state.projects = initialState.projects;
      state.project_slug = undefined;
      resetURLParams();
    },
    setProjectSlug: (state, action) => {
      const projectSlug = action.payload;
      console.log(`Setting project to ${projectSlug}`);
      if (isProjectIdWellFormed(projectSlug)) {
        state.project_slug = projectSlug;
        if (!state.projects[state.project_slug]) {
          state.projects[state.project_slug] = { ...workflowsInitialState };
        }
        setUrlFromState(state, true);
      } else if (projectSlug.length > 0) {
        throw new Error(`Project slug not well formed ${projectSlug}`);
      }
    },
    setWorkflow: (state, action) => {
      const workflowId = action.payload;
      const previousWorkflow = state.current;
      if (isWorkflowRecognizable(workflowId)) {
        console.log(`setWorkflow ${workflowId}`);
        state.current = workflowId;
        getWorkflowState(state, true);
        const workflowInfo = workflowLookup[workflowId];
        if (workflowInfo) {
          workflowInfo.switchToState(previousWorkflow, state);
        }
        setUrlFromState(state, true);
      } else if (workflowId.length > 0) {
        throw new Error(`Workflow not well formed ${workflowId}`);
      }
    },
    setInColorBlindMode: (state, action) => {
      state.in_color_blind_mode = action.payload;
    },
    setTargetDate: (state, action) => {
      if (state.current) {
        const sub = getWorkflowState(state, true);
        sub.targetDate = action.payload;
        // using only start date, so clear end date
        sub.targetEndDate = undefined;
        setUrlFromState(state);
      }
    },
    setStartEndDate: (
      state,
      action: PayloadAction<{ start: string; end: string }>,
    ) => {
      if (!state.current) return;
      const sub = getWorkflowState(state, true);
      sub.targetDate = action.payload.start;
      sub.targetEndDate = action.payload.end;
      setUrlFromState(state);
    },
    setTypicalMonth: (state, action) => {
      if (state.current) {
        getWorkflowState(state, true).typicalMonth = action.payload;
        setUrlFromState(state);
      }
    },
    setComparison: (state, action) => {
      if (state.current) {
        const sub = getWorkflowState(state, true);
        sub.comparison = action.payload;
        // using only start date, so clear end date
        sub.comparisonEndDate = undefined;
        setUrlFromState(state);
      }
    },
    setComparisonStartEndDate: (
      state,
      action: PayloadAction<{ start: string; end: string }>,
    ) => {
      if (!state.current) return;
      const sub = getWorkflowState(state, true);
      sub.comparison = action.payload.start;
      sub.comparisonEndDate = action.payload.end;
      setUrlFromState(state);
    },
    setSelectedSegmentId: (state, action) => {
      if (state.current) {
        getWorkflowState(state, true).segmentId = action.payload;
        setUrlFromState(state);
      }
    },
    setRouteSelection: (state, action) => {
      if (state.current) {
        const substate = getWorkflowState(state, true);
        if (!substate.routeSelectionHistory) {
          substate.routeSelectionHistory = [undefined];
        }
        // Push old selection history
        if (
          // Because a single segment selected does not count as a route selection, we infer the first step being a single segment route selection
          !routeSelectionIsSavedRoute(action.payload) && // If the new selection is a waypoint route
          substate.routeSelectionHistory[
            substate.routeSelectionHistory.length - 1
          ] === undefined // If the previous step was "no selection"
        ) {
          substate.routeSelectionHistory.push([action.payload[0]]); // Push the first route segment as the old route selection
        } else {
          substate.routeSelectionHistory.push(substate.routeSelection);
        }
        if (substate.routeSelectionHistory.length > kMaxRouteSelectionHistory) {
          // Limit the route selection undo history
          substate.routeSelectionHistory = substate.routeSelectionHistory.slice(
            substate.routeSelectionHistory.length - kMaxRouteSelectionHistory,
          );
        }
        substate.routeSelection = action.payload;

        setUrlFromState(state);
      }
    },
    undoRouteSelection: (state) => {
      if (state.current) {
        const substate = getWorkflowState(state, true);
        let selection = substate.routeSelectionHistory.pop();
        if ((selection || []).length > 0) {
          if (!routeSelectionIsSavedRoute(selection)) {
            selection = [...selection];
          }
        } else {
          selection = undefined;
        }
        substate.routeSelection = selection;
        if (selection && !routeSelectionIsSavedRoute(selection)) {
          substate.segmentId = selection[selection.length - 1];
        } else {
          substate.segmentId = undefined;
        }
        setUrlFromState(state);
      }
    },
    setMapCenter: (state, action) => {
      if (state.current) {
        const { lat, lng } = action.payload;
        // console.debug(`Setting map center ${lat} ${lng}`);
        getProjectState(state, true).mapCenter = { lat, lng };
        setUrlFromState(state);
      }
    },
    setMapZoom: (state, action) => {
      if (state.current) {
        getProjectState(state, true).mapZoom = action.payload;
        setUrlFromState(state);
      }
    },
    setRoadFilters: (state, action) => {
      if (state.current) {
        getProjectState(state, true).roadFilters = action.payload;
        setUrlFromState(state);
      }
    },
    setSlowdownThreshold: (state, action) => {
      if (state.current) {
        getWorkflowState(state, true).slowdownThreshold = action.payload;
        setUrlFromState(state);
      }
    },
    setMaximize: (state, action) => {
      console.assert(
        [MAXIMIZE_MAP, MAXIMIZE_DATA, null].includes(action.payload),
      );
      if (state.current) {
        getWorkflowState(state, true).maximize = action.payload;
        setUrlFromState(state);
      }
    },
    setHideSidebar: (state, action) => {
      state.hideSidebar = action.payload;
    },
    setMapStyle: (state, action) => {
      console.assert(action.payload === null || MapStyle[action.payload]);
      if (state.current) {
        getProjectState(state, true).mapStyle = action.payload;
      }
    },
    setShowBarbs: (state, action) => {
      if (state.current) {
        getProjectState(state, true).showBarbs = action.payload;
      }
    },
    setBinSize: (state, action) => {
      console.assert([BIN_15_MINS, BIN_60_MINS].includes(action.payload));
      if (state.current === WorkflowType.Plan) {
        getWorkflowState(state, true).binSize = action.payload;
        setUrlFromState(state);
      }
    },
    setCapacityThreshold: (state, action) => {
      if (state.current === WorkflowType.Plan) {
        const newValue = Math.abs(
          Number(action.payload) || kDefaultCapacityThreshold,
        );
        console.log(`setCapacityThreshold: ${newValue}`);
        getWorkflowState(state, true).capacityThreshold = newValue;
        setUrlFromState(state);
      }
    },
    setCapacityLanes: (state, action) => {
      if (state.current === WorkflowType.Plan) {
        const newValue = Math.abs(
          Number(action.payload) || kDefaultCapacityLanes,
        );
        console.log(`setCapacityLanes: ${newValue}`);
        getWorkflowState(state, true).capacityLanes = newValue;
        setUrlFromState(state);
      }
    },
    setVizMode: (state, action) => {
      console.assert([VIZ_MODE_TABLE, VIZ_MODE_CHART].includes(action.payload));
      if (state.current === WorkflowType.Plan) {
        getWorkflowState(state, true).vizMode = action.payload;
        setUrlFromState(state);
      }
    },
    setTimeOfDay: (state, action) => {
      if (action.payload !== undefined) {
        console.assert(action.payload >= -3 * 60);
        console.assert(action.payload <= (24 + 3) * 60);
      }
      if (workflowHasTimeOfDay(state.current)) {
        getWorkflowState(state, true).timeOfDay = action.payload;
      }
    },
    setTimelineMetric: (state, action) => {
      if (WorkflowType.isTimelineIsh(state.current)) {
        getWorkflowState(state, true).timelineMetric = action.payload;
        setUrlFromState(state);
      }
    },
    setCurrentScenarioId: (state, action) => {
      getWorkflowState(state, true).currentScenarioId = action.payload;
      setUrlFromState(state);
    },
    setCurrentAnalysisId: (state, action) => {
      getWorkflowState(state, true).currentAnalysisId = action.payload;
      setUrlFromState(state);
    },
    setRmStartDateTime: (state, action) => {
      getWorkflowState(state, true).startDateTime = action.payload;
      setUrlFromState(state);
    },
    setRmEndDateTime: (state, action) => {
      getWorkflowState(state, true).endDateTime = action.payload;
      setUrlFromState(state);
    },
    setRmDateTimeRange: (
      state,
      action: PayloadAction<{ start: string; end: string }>,
    ) => {
      getWorkflowState(state, true).startDateTime = action.payload.start;
      getWorkflowState(state, true).endDateTime = action.payload.end;
      setUrlFromState(state);
    },
    setScenarioSetupUIMode: (state, action) => {
      getWorkflowState(state, true).scenarioMode = action.payload;
    },
    setWorkflowTravelTimeToolbarUIMode: (state, action) => {
      getWorkflowState(state, true).workflowTravelTimeToolbarUIMode =
        action.payload;
    },
    setShowIncidents: (state, action) => {
      if (WorkflowType.isTimelineIsh(state.current)) {
        getWorkflowState(state, true).showIncidents = action.payload;
      }
    },
    setStateFromSearchParams: (state, action) => {
      const searchParams = new URLSearchParams(action.payload);
      console.log(`################# setStateFromSearchParams ${searchParams}`);
      const projectId = searchParams.get(kProjectParam);
      if (projectId && isProjectIdWellFormed(projectId)) {
        state.project_slug = projectId;
      }
      if (searchParams.has(kLegendSettingsParam)) {
        const t = searchParams.get(kLegendSettingsParam);
        if (t[0] === 'd' || t[0] === 'c') {
          state.legendSettings.type = t[0] === 'd' ? 'discrete' : 'continuous';
        }
        if (t[1] === 'g' || t[1] === 'n') {
          state.legendSettings.scheme = t[1] === 'g' ? 'green' : 'neutral';
        }
      } else {
        state.legendSettings = {
          ...defaultLegendSettings,
          ...state.legendSettings,
        };
      }

      const workflowId = searchParams.get(kWorkflowParam);
      if (workflowId && isWorkflowRecognizable(workflowId)) {
        state.current = workflowId;

        if (searchParams.has(kMapLatParam) && searchParams.has(kMapLatParam)) {
          getProjectState(state, true).mapCenter = {
            lat: Number(searchParams.get(kMapLatParam)),
            lng: Number(searchParams.get(kMapLngParam)),
          };
        }
        if (searchParams.has(kMapZoomParam)) {
          getProjectState(state, true).mapZoom = Number(
            searchParams.get(kMapZoomParam),
          );
        }
        if (searchParams.has(kRoadClassParam)) {
          getProjectState(state, true).roadFilters = searchParams
            .get(kRoadClassParam)
            ?.split('-');
        }
        if (searchParams.has(kDateParam)) {
          getWorkflowState(state, true).targetDate =
            searchParams.get(kDateParam);
        }
        if (searchParams.has(kTargetEndDateParam)) {
          getWorkflowState(state, true).targetEndDate =
            searchParams.get(kTargetEndDateParam);
        }
        if (searchParams.has(kTypicalMonthParam)) {
          if (!searchParams.has(kComparisonParam)) {
            getWorkflowState(state, true).comparison =
              searchParams.get(kTypicalMonthParam);
          }
        }
        if (searchParams.has(kComparisonParam)) {
          getWorkflowState(state, true).comparison =
            searchParams.get(kComparisonParam);
        }
        if (searchParams.has(kComparisonEndDateParam)) {
          getWorkflowState(state, true).comparisonEndDate = searchParams.get(
            kComparisonEndDateParam,
          );
        }
        if (searchParams.has(kStartDateParam)) {
          getWorkflowState(state, true).startDateTime =
            `${searchParams.get(kStartDateParam)}:00:00`;
        }
        if (searchParams.has(kEndDateParam)) {
          getWorkflowState(state, true).endDateTime =
            `${searchParams.get(kEndDateParam)}:00:00`;
        }
        if (searchParams.has(kSelectedSegmentParam)) {
          getWorkflowState(state, true).segmentId = searchParams.get(
            kSelectedSegmentParam,
          );
        }
        if (searchParams.has(kRouteSelectedParam)) {
          let route: any = searchParams.get(kRouteSelectedParam);
          // We can tell if this contains a segment ID vs a route ID because of _s, which are always in segment IDs but never in route hash Ids
          if (route.includes('_')) {
            route = route.split(',');
          }
          getWorkflowState(state, true).routeSelection = route;
        }
        if (searchParams.has(kSlowdownThresholdParam)) {
          getWorkflowState(state, true).slowdownThreshold = Number(
            searchParams.get(kSlowdownThresholdParam),
          );
        }
        if (searchParams.has(kMaximizeParam)) {
          getWorkflowState(state, true).maximize =
            searchParams.get(kMaximizeParam);
        }
        if (workflowId === WorkflowType.Plan) {
          if (searchParams.has(kBinSizeParam)) {
            getProjectState(state, true).workflows[WorkflowType.Plan].binSize =
              searchParams.get(kBinSizeParam);
          }
          if (searchParams.has(kCapacityThresholdParam)) {
            getProjectState(state, true).workflows[
              WorkflowType.Plan
            ].capacityThreshold = Number(
              searchParams.get(kCapacityThresholdParam),
            );
          }
          if (searchParams.has(kCapacityLanesParam)) {
            getProjectState(state, true).workflows[
              WorkflowType.Plan
            ].capacityLanes = Number(searchParams.get(kCapacityLanesParam));
          }
          if (searchParams.has(kVizModeParam)) {
            getProjectState(state, true).workflows[WorkflowType.Plan].vizMode =
              searchParams.get(kVizModeParam);
          }
        }
        if (
          WorkflowType.isTimelineIsh(
            // @ts-ignore
            WorkflowType[workflowId as keyof typeof WorkflowType],
          )
        ) {
          if (searchParams.has(kTimelineMetricParam)) {
            getProjectState(state, true).workflows[
              WorkflowType.Timeline
            ].timelineMetric = searchParams.get(kTimelineMetricParam);
          }
        }
        if (workflowId === WorkflowType.Detouring) {
          const detouringWorkflowState = getSpecificWorkflowState(
            state,
            WorkflowType.Detouring,
            true,
          );
          if (detouringWorkflowState) {
            detouringWorkflowState.currentScenarioId =
              searchParams.get(kScenarioIdParam) ?? undefined;
          }
        }
        if (workflowId === WorkflowType.OriginDestination) {
          const originDestinationWorkflowState = getSpecificWorkflowState(
            state,
            WorkflowType.OriginDestination,
            true,
          );
          if (originDestinationWorkflowState) {
            originDestinationWorkflowState.currentAnalysisId =
              searchParams.get(kAnalysisIdParam) ?? undefined;
            originDestinationWorkflowState.startTime =
              searchParams.get(kStartTimeParam) ?? DEFAULT_TIME;
            originDestinationWorkflowState.endTime =
              searchParams.get(kEndTimeParam) ?? DEFAULT_TIME;
          }
        }
        setUrlFromState(state);
      }
    },
    setEndTime: (state, action) => {
      getWorkflowState(state, true).endTime = action.payload;
      setUrlFromState(state);
    },
    setStartTime: (state, action) => {
      getWorkflowState(state, true).startTime = action.payload;
      setUrlFromState(state);
    },
    setDayType: (state, action) => {
      getWorkflowState(state, true).dayType = action.payload;
      setUrlFromState(state);
    },
  },
});

const { actions, reducer } = workflowSlice;
export const {
  updateLegendSettingsGlobal,
  resetWorkflowState,
  setProjectSlug,
  setWorkflow,
  setInColorBlindMode,
  setTargetDate,
  setStartEndDate,
  setTypicalMonth,
  setComparison,
  setComparisonStartEndDate,
  setSelectedSegmentId,
  setRouteSelection,
  undoRouteSelection,
  setMapCenter,
  setMapZoom,
  setRoadFilters,
  setSlowdownThreshold,
  setMaximize,
  setMapStyle,
  setShowBarbs,
  setBinSize,
  setCapacityThreshold,
  setCapacityLanes,
  setStateFromSearchParams,
  setVizMode,
  setTimeOfDay,
  setTimelineMetric,
  setShowIncidents,
  setWorkflowTravelTimeToolbarUIMode,
  setCurrentScenarioId,
  setScenarioSetupUIMode,
  setRmStartDateTime,
  setRmEndDateTime,
  setRmDateTimeRange,
  setCurrentAnalysisId,
  setEndTime,
  setStartTime,
  setDayType,
  setHideSidebar,
} = actions;
export default reducer;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useAppSelector((state: RootState) => state.counter.value)`

export const selectTargetDate = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getWorkflowState(workflow)?.targetDate;
};

export const selectTargetEndDate = (rootState) => {
  const { workflow } = rootState;
  return (
    (workflow.current && getWorkflowState(workflow)?.targetEndDate) || undefined
  );
};

export const selectComparisonEndDate = (rootState) => {
  const { workflow } = rootState;
  return (
    (workflow.current && getWorkflowState(workflow)?.comparisonEndDate) ||
    undefined
  );
};

export const selectTypicalMonth = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getWorkflowState(workflow)?.typicalMonth;
};

export const selectComparison = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getWorkflowState(workflow)?.comparison;
};

export const selectUseDayRange = (rootState) => {
  const targetDate = selectTargetDate(rootState);
  const targetEndDate = selectTargetEndDate(rootState);
  return Boolean(targetDate && targetEndDate);
};

export const selectIsMonthComparison = (rootState) => {
  const comparison = selectComparison(rootState);
  return Boolean(comparison && isComparisonMonth(comparison));
};

export const selectUseComparisonDayRange = (rootState) => {
  const comparison = selectComparison(rootState);
  const comparisonEndDate = selectComparisonEndDate(rootState);
  return Boolean(
    comparison && !isComparisonMonth(comparison) && comparisonEndDate,
  );
};

export const selectWorkflow = (rootState) => {
  const { workflow } = rootState;
  return workflow.current;
};

export const selectProject = (rootState) => {
  const { workflow } = rootState;
  return workflow.project_slug;
};

export const selectLegendSettings = (rootState) => {
  const { workflow } = rootState;
  return workflow.legendSettings || {};
};

export const selectInColorBlindMode = (rootState) => {
  const { workflow } = rootState;
  return workflow.in_color_blind_mode || false;
};

export const selectCurrentProjectInfo = (rootState) => {
  return getProjectInfoForSlug(
    selectUserState(rootState),
    selectProject(rootState),
  );
};

export const selectProjectSpecialDates = (rootState) => {
  return selectCurrentProjectInfo(rootState)?.special_date_requests ?? [];
};

export const selectShortestAvailableBin = (rootState) => {
  const projectInfo = selectCurrentProjectInfo(rootState);
  if (projectInfo) {
    return Math.min(...projectInfo.available_bins);
  } else {
    return undefined;
  }
};

export const selectSupportMinutes = (rootState) => {
  const minBin = selectShortestAvailableBin(rootState);
  return minBin === BE_BIN_15_MINS;
};

export const selectSegmentId = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getWorkflowState(workflow)?.segmentId;
};

/**
 *
 * @param rootState
 * @returns The selected savedRouteId: string if this is a savedRoute, else a list of segmentIds: string[], else undefined
 */
export const selectRouteSelection = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getWorkflowState(workflow)?.routeSelection;
};

export const selectMapCenter = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getProjectState(workflow)?.mapCenter;
};

export const selectMapZoom = (rootState): number | null => {
  const { workflow } = rootState;
  return workflow.current && getProjectState(workflow)?.mapZoom;
};

export const selectRoadFilters = (rootState): string[] | null => {
  const { workflow } = rootState;
  return workflow.current && getProjectState(workflow)?.roadFilters;
};

export const selectSlowdownThreshold = (rootState) => {
  const { workflow } = rootState;
  // return workflow.current ? getWorkflowState(workflow)?.slowdownThreshold : null;
  return (
    workflow.current &&
    (getWorkflowState(workflow)?.slowdownThreshold ?? kDefaultSlowdownThreshold)
  );
};

export const selectMaximize = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getWorkflowState(workflow)?.maximize;
};

export const selectMapStyle = (rootState) => {
  const { workflow } = rootState;
  return workflow.current && getProjectState(workflow)?.mapStyle;
};

export const selectShowBarbs = (rootState): boolean => {
  const { workflow } = rootState;
  return (workflow.current && getProjectState(workflow)?.showBarbs) || false;
};

export const selectBinSize = (rootState) => {
  const { workflow } = rootState;
  return (
    workflow.current === WorkflowType.Plan &&
    getSpecificWorkflowState(workflow, WorkflowType.Plan, false)?.binSize
  );
};

export const selectCapacityThreshold = (rootState) => {
  const { workflow } = rootState;
  return (
    workflow.current === WorkflowType.Plan &&
    getSpecificWorkflowState(workflow, WorkflowType.Plan, false)
      ?.capacityThreshold
  );
};

export const selectCapacityLanes = (rootState) => {
  const { workflow } = rootState;
  return (
    workflow.current === WorkflowType.Plan &&
    getSpecificWorkflowState(workflow, WorkflowType.Plan, false)?.capacityLanes
  );
};

export const selectVizMode = (rootState) => {
  const { workflow } = rootState;
  return (
    workflow.current === WorkflowType.Plan &&
    getSpecificWorkflowState(workflow, WorkflowType.Plan, false)?.vizMode
  );
};

const workflowHasTimeOfDay = (workflow: WorkflowType) =>
  WorkflowType.isTimelineIsh(workflow) || WorkflowType.Queuing === workflow;

export const selectTimeOfDay = (rootState) => {
  const { workflow } = rootState;
  return (
    workflowHasTimeOfDay(workflow.current) &&
    getSpecificWorkflowState(workflow, workflow.current, false)?.timeOfDay
  );
};

export const selectTimelineMetric = (rootState) => {
  const { workflow } = rootState;
  return (
    WorkflowType.isTimelineIsh(workflow.current) &&
    getSpecificWorkflowState(workflow, workflow.current, false)?.timelineMetric
  );
};

export const selectShowIncidents = (rootState) => {
  const { workflow } = rootState;
  return (
    WorkflowType.isTimelineIsh(workflow.current) &&
    getSpecificWorkflowState(workflow, workflow.current, false)?.showIncidents
  );
};

export const selectRmStartDateTime = (rootState) => {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.startDateTime;
};

export const selectRmEndDateTime = (rootState) => {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.endDateTime;
};

export const selectCurrentScenarioId = (rootState) => {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.currentScenarioId;
};

export const selectCurrentAnalysisId = (rootState) => {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.currentAnalysisId;
};

export function selectScenarioSetupUIMode(rootState) {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.scenarioMode;
}

export function selectWorkflowTravelTimeToolbarUIMode(rootState) {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.workflowTravelTimeToolbarUIMode;
}

export const selectEndTime = (rootState) => {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.endTime;
};
export const selectStartTime = (rootState) => {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.startTime;
};
export const selectDayType = (rootState) => {
  const { workflow } = rootState;
  return getWorkflowState(workflow)?.dayType;
};
export const selectHideSidebar = (rootState) => {
  const { workflow } = rootState;
  return workflow.hideSidebar || false;
};
